/* =============================================================================
 * ADA / WCAG 2.2 AA runtime remediation
 * -----------------------------------------------------------------------------
 * Added for the 2026-09-15 accessibility audit of holtzleather.com.
 *
 * Why a runtime pass exists at all:
 *   A large share of the audit's findings are on markup that does not exist in
 *   this repository at build time - Flickity/plyr rebuild the product gallery
 *   after load, and several storefront apps (Klaviyo, Yotpo, LiveChat, Globo
 *   product options, primeBadges) inject their own DOM. Those nodes can only be
 *   corrected after they appear, which is what this file does.
 *
 * Why it is a separate asset:
 *   Earlier rounds of this work lived in utilities.js and were silently lost
 *   twice when the live theme was pulled back down over the working tree. This
 *   file is not part of the vendor bundle, so it survives those pulls.
 *
 * Nothing here changes layout, copy or behaviour that a sighted mouse user can
 * perceive: it only adds names, roles and states to elements that had none.
 * ============================================================================= */
(function () {
  'use strict';

  var THROTTLE_MS = 200;

  /* ---------------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------------ */

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  var NON_RENDERED = { NOSCRIPT: 1, SCRIPT: 1, STYLE: 1, TEMPLATE: 1 };

  function renderedText(el) {
    var text = '';
    for (var i = 0; i < el.childNodes.length; i++) {
      var node = el.childNodes[i];
      if (node.nodeType === 3) {
        text += node.textContent;
      } else if (node.nodeType === 1) {
        if (NON_RENDERED[node.nodeName]) continue;
        if (node.getAttribute('aria-hidden') === 'true') continue;
        text += renderedText(node);
      }
    }
    return text;
  }

  function hasAccessibleName(el) {
    if (!el) return true;
    if (el.getAttribute('aria-label') && el.getAttribute('aria-label').trim()) return true;
    if (el.getAttribute('aria-labelledby')) return true;
    if (renderedText(el).trim()) return true;
    var img = el.querySelector('img[alt]');
    if (img && img.getAttribute('alt').trim()) return true;
    var svgTitle = el.querySelector('svg > title');
    if (svgTitle && (svgTitle.textContent || '').trim()) return true;
    return false;
  }

  function labelFromHref(href) {
    if (!href) return '';
    try {
      var url = new URL(href, window.location.origin);
      var segments = url.pathname.split('/').filter(Boolean);
      var last = segments.pop() || url.hostname;
      return last
        .replace(/[-_]+/g, ' ')
        .replace(/\.[a-z0-9]+$/i, '')
        .replace(/\b\w/g, function (c) {
          return c.toUpperCase();
        })
        .trim();
    } catch (e) {
      return '';
    }
  }

  function isMeaningfulLabel(text) {
    if (!text) return false;
    var trimmed = text.trim();
    if (trimmed.length < 2) return false;
    if (/^[0-9a-f]{8,}$/i.test(trimmed.replace(/\s+/g, ''))) return false;
    if (!/[A-Za-z]{3,}/.test(trimmed)) return false;
    var letters = trimmed.replace(/[^A-Za-z]/g, '').length;
    var digits = trimmed.replace(/[^0-9]/g, '').length;
    return letters > digits;
  }

  function labelFromHost(href) {
    if (!href) return '';
    try {
      var url = new URL(href, window.location.origin);
      if (url.hostname === window.location.hostname) return '';
      var parts = url.hostname.replace(/^www\./i, '').split('.');
      var name = parts.length > 2 ? parts[parts.length - 2] : parts[0];
      return name
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, function (c) {
          return c.toUpperCase();
        })
        .trim();
    } catch (e) {
      return '';
    }
  }

  function titleFromIframeSrc(src) {
    if (!src) return 'Embedded content';
    if (/youtube|youtu\.be/i.test(src)) return 'YouTube video player';
    if (/vimeo/i.test(src)) return 'Vimeo video player';
    if (/spotify/i.test(src)) return 'Spotify player';
    if (/soundcloud/i.test(src)) return 'SoundCloud player';
    if (/google\.com\/maps|maps\.google/i.test(src)) return 'Google Maps';
    if (/calendar/i.test(src)) return 'Calendar';
    if (/livechat/i.test(src)) return 'Live chat widget';
    if (/shopify|web-pixel/i.test(src)) return 'Shopify analytics frame';
    return 'Embedded content';
  }

  /* ---------------------------------------------------------------------------
   * 1. aria-hidden must never contain focusable content
   *    WCAG 4.1.2 - audit rules: aria-hidden-focus (110), keyboard-trap (21)
   *
   *    Flickity marks off-screen carousel cells `aria-hidden="true"` while the
   *    cells (and their links/buttons) keep `tabindex="0"`. Tabbing then lands
   *    on a cell that assistive tech cannot see, which is what the audit's
   *    keyboard probe reported as a trap on `.product-gallery__thumbnail`.
   *
   *    The original tabindex is stashed so it is restored verbatim when the
   *    cell scrolls back into view - the carousel keeps working exactly as
   *    before for mouse and touch.
   * ------------------------------------------------------------------------ */
  var FOCUSABLE =
    'a[href], area[href], input, select, textarea, button, iframe, object, embed, ' +
    'summary, audio[controls], video[controls], [tabindex], [contenteditable="true"]';

  function syncAriaHiddenFocusability(root) {
    each(root.querySelectorAll('[aria-hidden="true"]'), function (hidden) {
      var candidates = hidden.matches(FOCUSABLE) ? [hidden] : [];
      candidates = candidates.concat(Array.prototype.slice.call(hidden.querySelectorAll(FOCUSABLE)));

      candidates.forEach(function (el) {
        if (el.getAttribute('tabindex') === '-1') return;
        if (!el.hasAttribute('data-a11y-tabindex')) {
          el.setAttribute('data-a11y-tabindex', el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : 'none');
        }
        el.setAttribute('tabindex', '-1');
        if (el === document.activeElement && typeof el.blur === 'function') {
          el.blur();
        }
      });
    });

    // Restore anything that is no longer inside an aria-hidden subtree.
    each(root.querySelectorAll('[data-a11y-tabindex]'), function (el) {
      if (el.closest('[aria-hidden="true"]')) return;
      var saved = el.getAttribute('data-a11y-tabindex');
      if (saved === 'none') {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', saved);
      }
      el.removeAttribute('data-a11y-tabindex');
    });
  }

  /* ---------------------------------------------------------------------------
   * 2. Keyboard activation for the product gallery thumbnails
   *    WCAG 2.1.1 Keyboard
   *
   *    The thumbnails are `<div role="button" tabindex="0">` (see
   *    snippets/product__images.liquid). A native click is dispatched on
   *    Enter/Space so the theme's existing click handler - and Flickity's
   *    asNavFor binding - select the matching slide. No new navigation logic.
   * ------------------------------------------------------------------------ */
  function bindThumbnailKeys() {
    if (document.documentElement.hasAttribute('data-a11y-thumb-keys')) return;
    document.documentElement.setAttribute('data-a11y-thumb-keys', 'true');

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var thumb = target.closest('.product-gallery__thumbnail[role="button"]');
      if (!thumb) return;
      event.preventDefault();
      thumb.click();
    });
  }

  /* ---------------------------------------------------------------------------
   * 3. Decorative, silent background videos
   *    WCAG 1.2.2 / 2.1.1 - audit rules: video-caption (48),
   *    media_keyboard_controllable (44)
   *
   *    These are the Webflow-exported hero loops (`data-wf-ignore`): muted,
   *    autoplaying, looping b-roll behind headline text, with no controls and
   *    no audio track. They carry no information that the surrounding copy does
   *    not already carry, so the correct treatment is to expose them as
   *    decorative rather than to invent a caption track for silent footage.
   *
   *    Product videos that a customer can actually play are NOT touched here -
   *    those need real caption files uploaded against the Shopify media.
   * ------------------------------------------------------------------------ */
  function markDecorativeVideos(root) {
    each(root.querySelectorAll('video'), function (video) {
      if (video.hasAttribute('data-a11y-decorative')) return;
      if (video.controls) return;
      if (video.querySelector('track')) return;

      var isBackgroundLoop =
        (video.hasAttribute('data-wf-ignore') || video.hasAttribute('data-object-fit')) &&
        video.muted &&
        video.loop;

      if (!isBackgroundLoop) return;

      video.setAttribute('data-a11y-decorative', 'true');
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('tabindex', '-1');
    });

    each(root.querySelectorAll('video[role="presentation"], video[role="none"]'), function (video) {
      video.removeAttribute('role');
    });
  }

  /* ---------------------------------------------------------------------------
   * 4. Names for controls that render without one
   *    WCAG 2.4.4 / 4.1.2 - audit rule: link-name
   *
   *    Covers app-injected markup that is not in this repository: Klaviyo popup
   *    buttons, `link-promo` banners, Webflow `w-inline-block` wrappers and any
   *    icon-only link the theme renders with a `title` but no text.
   *    An element that already has a name is never rewritten, so this can never
   *    create a WCAG 2.5.3 Label-in-Name mismatch.
   * ------------------------------------------------------------------------ */
  function nameEmptyControls(root) {
    each(root.querySelectorAll('a, button, [role="button"], [role="link"]'), function (el) {
      if (el.hasAttribute('data-a11y-named')) return;
      if (hasAccessibleName(el)) return;

      var img = el.querySelector('img[src], img[data-src]');
      var candidates = [
        (el.getAttribute('title') || '').trim(),
        labelFromHost(el.getAttribute('href')),
        labelFromHref(el.getAttribute('href')),
        img ? labelFromHref(img.getAttribute('src') || img.getAttribute('data-src')) : '',
      ];

      var name = '';
      for (var i = 0; i < candidates.length; i++) {
        if (isMeaningfulLabel(candidates[i])) {
          name = candidates[i].trim();
          break;
        }
      }

      if (!name) return;
      el.setAttribute('aria-label', name);
      el.setAttribute('data-a11y-named', 'true');
    });
  }

  /* ---------------------------------------------------------------------------
   * 5. Images and frames injected by apps
   *    WCAG 1.1.1 - audit rule: image-alt (Klaviyo popup artwork)
   *    WCAG 4.1.2 - audit rule: frame_src_valid / frame-tested (LiveChat)
   * ------------------------------------------------------------------------ */
  function fixImagesAndFrames(root) {
    each(root.querySelectorAll('img:not([alt])'), function (img) {
      var src = img.getAttribute('src') || '';
      if (/logo/i.test(src)) {
        img.setAttribute('alt', document.title.split('–')[0].trim() || 'Logo');
      } else {
        // Decorative by default: an invented description is worse than none.
        img.setAttribute('alt', '');
      }
    });

    each(root.querySelectorAll('img[alt=""][tabindex], img[role="presentation"][tabindex], img[role="none"][tabindex]'), function (img) {
      img.removeAttribute('tabindex');
    });

    // Tracking pixels and preview thumbnails are never meaningful content.
    each(root.querySelectorAll('img[width="1"][height="1"], img[alt=""][data-preview-image]'), function (img) {
      img.setAttribute('aria-hidden', 'true');
    });

    each(root.querySelectorAll('iframe:not([title]), iframe[title=""]'), function (frame) {
      frame.setAttribute('title', titleFromIframeSrc(frame.getAttribute('src')));
    });
  }

  /* ---------------------------------------------------------------------------
   * 6. Malformed nested lists in merchant-written product descriptions
   *    WCAG 1.3.1 - audit rule: list ("<ul> and <ol> must only directly contain
   *    <li>, <script> or <template> elements")
   *
   *    Several product descriptions written in the Shopify admin rich-text
   *    editor contain `<ul><li>Dimensions</li><ul><li>Strap length</li></ul></ul>`.
   *    The nested list is a sibling of the <li> it belongs to instead of a child
   *    of it, so the sub-list is orphaned in the accessibility tree.
   *
   *    Moving the stray list inside the <li> immediately before it is what the
   *    author meant, and browsers already paint it that way, so the visible
   *    indentation does not move. This runs in the page rather than being
   *    corrected once in the admin because the descriptions are content: a
   *    merchant edit would reintroduce the problem.
   * ------------------------------------------------------------------------ */
  var LIST_CHILD_ALLOWED = { LI: 1, SCRIPT: 1, TEMPLATE: 1 };

  function orphanListItem(doc) {
    var wrapper = doc.createElement('li');
    wrapper.setAttribute('data-a11y-list-orphan', 'true');
    return wrapper;
  }

  function repairNestedLists(root) {
    each(root.querySelectorAll('ul > ul, ul > ol, ol > ul, ol > ol'), function (stray) {
      var previous = stray.previousElementSibling;
      while (previous && previous.tagName !== 'LI') {
        previous = previous.previousElementSibling;
      }
      if (previous) {
        previous.appendChild(stray);
        return;
      }
      // No preceding <li> to attach to - give the orphan one of its own.
      var wrapper = document.createElement('li');
      stray.parentNode.insertBefore(wrapper, stray);
      wrapper.appendChild(stray);
    });

    each(root.querySelectorAll('ul, ol'), function (list) {
      var doc = list.ownerDocument;
      var node = list.firstChild;

      while (node) {
        var next = node.nextSibling;

        if (node.nodeType === 1 && !LIST_CHILD_ALLOWED[node.tagName]) {
          var wrapper = orphanListItem(doc);
          list.insertBefore(wrapper, node);
          wrapper.appendChild(node);
        } else if (node.nodeType === 3 && node.textContent.trim()) {
          var textWrapper = orphanListItem(doc);
          list.insertBefore(textWrapper, node);
          textWrapper.appendChild(node);
        }

        node = next;
      }
    });
  }

  /* ---------------------------------------------------------------------------
   * 7. Dropdown state on the main navigation
   *    WCAG 4.1.2 - keeps the aria-expanded added in the header sections honest.
   *
   *    The markup ships `aria-expanded="false"`; without this the value would be
   *    permanently stale, which is worse than not exposing the state at all.
   * ------------------------------------------------------------------------ */
  function bindNavExpandedState() {
    if (document.documentElement.hasAttribute('data-a11y-nav-state')) return;
    document.documentElement.setAttribute('data-a11y-nav-state', 'true');

    function setState(item, expanded) {
      if (!item) return;
      each(item.querySelectorAll('[aria-haspopup="true"]'), function (link) {
        link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    ['mouseenter', 'focusin'].forEach(function (type) {
      document.addEventListener(
        type,
        function (event) {
          if (!event.target || typeof event.target.closest !== 'function') return;
          var item = event.target.closest('.navbar-item.has-dropdown, .vertical-header__first-level.has-dropdown');
          if (item) setState(item, true);
        },
        true
      );
    });

    ['mouseleave', 'focusout'].forEach(function (type) {
      document.addEventListener(
        type,
        function (event) {
          if (!event.target || typeof event.target.closest !== 'function') return;
          var item = event.target.closest('.navbar-item.has-dropdown, .vertical-header__first-level.has-dropdown');
          if (!item) return;
          if (type === 'focusout' && item.contains(event.relatedTarget)) return;
          setState(item, false);
        },
        true
      );
    });
  }

  var NAV_TRIGGERS =
    '.navbar-item.has-dropdown > label > a.navbar-link:not([href]), .navbar-item.has-mega-menu > label > a.navbar-link:not([href])';

  function exposeNavTriggers(root) {
    each(root.querySelectorAll(NAV_TRIGGERS), function (link) {
      if (!link.textContent.trim()) return;
      if (!link.hasAttribute('role')) link.setAttribute('role', 'button');
      if (!link.hasAttribute('tabindex')) link.setAttribute('tabindex', '0');
      if (!link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', 'false');
    });
  }

  function navItemIsOpen(item) {
    if (item.classList.contains('show-dropdown')) return true;
    if (item.querySelector('.mega-menu__section.is-active, .mega-menu.mega-menu--show')) return true;
    return !!item.closest('.dropdown-click--true') && item.classList.contains('is-opened');
  }

  function syncNavTriggerState() {
    each(document.querySelectorAll(NAV_TRIGGERS), function (link) {
      if (link.getAttribute('role') !== 'button') return;
      var item = link.closest('.navbar-item');
      var open = item && navItemIsOpen(item) ? 'true' : 'false';
      if (link.getAttribute('aria-expanded') !== open) link.setAttribute('aria-expanded', open);
    });
  }

  function closeNavItem(item, trigger) {
    if (item.classList.contains('is-opened') && trigger) trigger.click();
    item.classList.remove('is-opened');
    item.classList.remove('show-dropdown');
    each(item.querySelectorAll('.mega-menu__section.is-active'), function (section) {
      section.classList.remove('is-active');
    });
    each(item.querySelectorAll('.mega-menu.mega-menu--show'), function (menu) {
      menu.classList.remove('mega-menu--show');
    });
  }

  function bindNavTriggerKeys() {
    if (document.documentElement.hasAttribute('data-a11y-nav-keys')) return;
    document.documentElement.setAttribute('data-a11y-nav-keys', 'true');

    document.addEventListener('keydown', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var item = target.closest('.navbar-item.has-dropdown, .navbar-item.has-mega-menu');
      if (!item) return;
      var trigger = item.querySelector(':scope > label > a.navbar-link[role="button"]');
      if (!trigger) return;

      if (event.key === 'Escape' || event.key === 'Esc') {
        if (!navItemIsOpen(item)) return;
        closeNavItem(item, trigger);
        if (target !== trigger) trigger.focus();
        syncNavTriggerState();
        return;
      }

      if (target !== trigger) return;
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();

      var clickMode = !!item.closest('.dropdown-click--true');
      var wasOpen = clickMode
        ? item.classList.contains('is-opened') || !!item.querySelector('.mega-menu__section.is-active')
        : trigger.getAttribute('aria-expanded') === 'true';

      if (wasOpen) {
        closeNavItem(item, trigger);
      } else {
        if (clickMode) trigger.click();
        item.classList.add('show-dropdown');
      }
      syncNavTriggerState();
    });

    document.addEventListener(
      'focusout',
      function (event) {
        var target = event.target;
        var next = event.relatedTarget;
        if (!target || !next || typeof target.closest !== 'function') return;
        var item = target.closest('.navbar-item.has-dropdown, .navbar-item.has-mega-menu');
        if (!item || item.contains(next)) return;
        var trigger = item.querySelector(':scope > label > a.navbar-link[role="button"]');
        if (!trigger || !navItemIsOpen(item)) return;
        closeNavItem(item, trigger);
        syncNavTriggerState();
      },
      true
    );

    document.addEventListener('click', function () {
      window.setTimeout(syncNavTriggerState, 0);
    });
  }

  var a11yIdCounter = 0;

  function syncPlyrToggleNames(root) {
    each(root.querySelectorAll('.plyr__control[data-plyr][aria-label]'), function (button) {
      if (!button.querySelector('.plyr__sr-only, .plyr__tooltip')) return;
      button.removeAttribute('aria-label');
    });

    each(root.querySelectorAll('.plyr__control--overlaid[data-plyr]:not([aria-labelledby])'), function (button) {
      var spans = button.querySelectorAll('.plyr__sr-only');
      if (spans.length !== 1 || button.querySelector('.label--pressed, .label--not-pressed')) return;
      var span = spans[0];
      if (!renderedText(span).trim()) return;
      if (!span.id) span.id = 'a11y-plyr-label-' + (++a11yIdCounter);
      span.hidden = true;
      button.setAttribute('aria-labelledby', span.id);
    });

    each(root.querySelectorAll('.plyr input[type="range"][data-plyr][aria-label]'), function (input) {
      if (!input.id) input.id = 'a11y-plyr-' + input.getAttribute('data-plyr') + '-' + (++a11yIdCounter);
      var label = document.createElement('label');
      label.className = 'a11y-visually-hidden';
      label.htmlFor = input.id;
      label.textContent = input.getAttribute('aria-label');
      input.parentNode.insertBefore(label, input);
      input.removeAttribute('aria-label');
    });

    each(root.querySelectorAll('.plyr progress.plyr__progress__buffer:not([aria-hidden])'), function (buffer) {
      buffer.setAttribute('aria-hidden', 'true');
    });

    each(root.querySelectorAll('.plyr[tabindex="0"]'), function (container) {
      container.setAttribute('tabindex', '-1');
    });

    each(root.querySelectorAll('button [role="tooltip"], [role="button"] [role="tooltip"]'), function (tip) {
      tip.removeAttribute('role');
    });
  }

  var MERCHANT_SCOPES = '.article-content, .rte, .page__content, .shopify-policy__body';

  var LINK_CONTRAST_SCOPES = MERCHANT_SCOPES + ', .studioengine';

  var NEW_WINDOW_NOTE = ' (opens in a new window)';
  var NEW_WINDOW_RE = /new\s+(window|tab)/i;

  var VAGUE_LINK_RE = /^(read more|reading more|continue reading|learn more|find out more|more info|more information|click here|see more|view more|more|here|this|details|link)$/;

  function precedingHeadingText(link, scope) {
    var headings = scope.querySelectorAll('h1, h2, h3, h4, h5, h6');
    var context = '';
    for (var i = 0; i < headings.length; i++) {
      if (!(headings[i].compareDocumentPosition(link) & 4)) break;
      var text = renderedText(headings[i]).replace(/\s+/g, ' ').trim();
      if (text) context = text;
    }
    return context;
  }

  function describeVagueLinks(root) {
    each(root.querySelectorAll(MERCHANT_SCOPES), function (scope) {
      each(scope.querySelectorAll('a[href]'), function (link) {
        if (link.hasAttribute('data-a11y-link-purpose')) return;
        if (link.getAttribute('aria-label') || link.getAttribute('aria-labelledby')) return;

        var text = renderedText(link).replace(/\s+/g, ' ').trim();
        var phrase = text.replace(NEW_WINDOW_NOTE, '').replace(/[\s.!:…]+$/, '');
        if (!VAGUE_LINK_RE.test(phrase.toLowerCase())) return;

        var context = precedingHeadingText(link, scope);
        if (!context) return;
        if (context.toLowerCase().indexOf(phrase.toLowerCase()) !== -1) return;

        link.setAttribute('aria-label', phrase + ': ' + context);
        link.setAttribute('data-a11y-link-purpose', 'described');
      });
    });
  }

  var NEW_WINDOW_NOTE_ID = 'a11y-new-window-note';

  function newWindowNote(doc) {
    var note = doc.getElementById(NEW_WINDOW_NOTE_ID);
    if (note) return note;
    note = doc.createElement('span');
    note.id = NEW_WINDOW_NOTE_ID;
    note.hidden = true;
    note.textContent = 'Opens in a new window';
    doc.body.appendChild(note);
    return note;
  }

  function warnExternalTargets(root) {
    each(root.querySelectorAll('a[target="_blank"]'), function (link) {
      if (link.hasAttribute('data-a11y-new-window')) return;

      var text = (link.textContent || '').replace(/\s+/g, ' ').trim();
      var title = (link.getAttribute('title') || '').trim();

      if (NEW_WINDOW_RE.test(text) || NEW_WINDOW_RE.test(title)) {
        link.setAttribute('data-a11y-new-window', 'skipped');
        return;
      }

      var label = (link.getAttribute('aria-label') || '').trim();
      if (label && NEW_WINDOW_RE.test(label)) {
        var bare = label.replace(/\s*\(?opens in a new (window|tab)\)?\s*$/i, '').trim();
        if (!bare) {
          link.setAttribute('data-a11y-new-window', 'skipped');
          return;
        }
        link.setAttribute('aria-label', bare);
      }

      var note = newWindowNote(link.ownerDocument);
      var described = (link.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (described.indexOf(note.id) === -1) described.push(note.id);
      link.setAttribute('aria-describedby', described.join(' '));
      link.setAttribute('data-a11y-new-window', 'true');
    });
  }

  function markEmptyHeadings(root) {
    each(root.querySelectorAll('h1, h2, h3, h4, h5, h6'), function (heading) {
      if (heading.hasAttribute('data-a11y-empty-heading')) return;
      if (heading.hasAttribute('role')) return;
      if (heading.getAttribute('aria-label') || heading.getAttribute('aria-labelledby')) return;
      if (renderedText(heading).replace(/\s| /g, '')) return;
      if (heading.querySelector('img[alt]:not([alt=""]), svg title, [aria-label]')) return;
      if (heading.querySelector(FOCUSABLE)) return;
      if (!heading.parentNode) return;

      var style = window.getComputedStyle(heading);
      var ownSize = parseFloat(style.fontSize) || 16;
      var parentSize = heading.parentElement
        ? parseFloat(window.getComputedStyle(heading.parentElement).fontSize) || ownSize
        : ownSize;

      var spacing =
        'font-size:' + (ownSize / parentSize).toFixed(4) + 'em;' +
        'margin-top:' + ((parseFloat(style.marginTop) || 0) / ownSize).toFixed(4) + 'em;' +
        'margin-bottom:' + ((parseFloat(style.marginBottom) || 0) / ownSize).toFixed(4) + 'em;';

      var spacer = heading.ownerDocument.createElement('div');
      for (var i = 0; i < heading.attributes.length; i++) {
        spacer.setAttribute(heading.attributes[i].name, heading.attributes[i].value);
      }
      spacer.setAttribute('style', (heading.getAttribute('style') || '') + ';' + spacing);
      spacer.setAttribute('data-a11y-empty-heading', heading.tagName.toLowerCase());
      spacer.innerHTML = heading.innerHTML;
      heading.parentNode.replaceChild(spacer, heading);
    });
  }

  var demotedHeadings = 0;

  function dedupeTopLevelHeadings(root) {
    var h1s = Array.prototype.slice.call(root.querySelectorAll('h1'));
    if (h1s.length < 2 || demotedHeadings > 20) return;

    var keep = null;
    for (var i = 0; i < h1s.length; i++) {
      if (!h1s[i].closest('.shopify-app-block, .shopify-block')) {
        keep = h1s[i];
        break;
      }
    }
    if (!keep) keep = h1s[0];

    h1s.forEach(function (h1) {
      if (h1 === keep || !h1.parentNode) return;

      var replacement = h1.ownerDocument.createElement('h2');
      for (var i = 0; i < h1.attributes.length; i++) {
        replacement.setAttribute(h1.attributes[i].name, h1.attributes[i].value);
      }
      replacement.setAttribute('data-a11y-demoted-from', 'h1');
      replacement.innerHTML = h1.innerHTML;
      h1.parentNode.replaceChild(replacement, h1);
      demotedHeadings++;
    });
  }

  function relativeLuminance(color) {
    var parts = String(color).match(/[\d.]+/g);
    if (!parts || parts.length < 3) return null;
    if (parts.length > 3 && Number(parts[3]) < 1) return null;
    var channels = parts.slice(0, 3).map(function (value) {
      value = Number(value) / 255;
      return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  }

  function contrastRatio(foreground, background) {
    var first = relativeLuminance(foreground);
    var second = relativeLuminance(background);
    if (first === null || second === null) return null;
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
  }

  function enforceAppButtonContrast(root) {
    each(root.querySelectorAll('.klaviyo-form-button'), function (button) {
      if (button.hasAttribute('data-a11y-contrast')) return;

      var foreground = window.getComputedStyle(button).color;
      var background = window.getComputedStyle(button).backgroundColor;
      if (relativeLuminance(background) === null) return;

      var ratio = contrastRatio(foreground, background);
      if (ratio === null || ratio >= 4.5) return;

      var channels = String(background).match(/[\d.]+/g).slice(0, 3).map(Number);
      var darken = relativeLuminance(foreground) > relativeLuminance(background);

      for (var step = 1; step <= 20; step++) {
        var shifted = channels.map(function (value) {
          return darken
            ? Math.round(value * (1 - step * 0.05))
            : Math.round(value + (255 - value) * step * 0.05);
        });
        var candidate = 'rgb(' + shifted.join(', ') + ')';
        if (contrastRatio(foreground, candidate) >= 4.5) {
          button.style.setProperty('background-color', candidate, 'important');
          button.setAttribute('data-a11y-contrast', 'adjusted');
          return;
        }
      }
    });
  }

  var CONTENT_SCOPES = '.article-content, .content, .rte, .product__description, .page__content';

  function covers(box, rect) {
    return (
      box.left <= rect.left &&
      box.top <= rect.top &&
      box.right >= rect.right &&
      box.bottom >= rect.bottom
    );
  }

  function paints(style) {
    if (style.backgroundImage && style.backgroundImage !== 'none') return true;
    return relativeLuminance(style.backgroundColor) !== null;
  }

  function hasCoveringLayer(container, rect) {
    var found = false;
    each(container.children, function (child) {
      if (found) return;

      var style = window.getComputedStyle(child);
      if (style.position !== 'absolute' && style.position !== 'fixed') return;
      if (!covers(child.getBoundingClientRect(), rect)) return;
      if (paints(style)) {
        found = true;
        return;
      }

      each(child.children, function (layer) {
        if (found) return;
        if (paints(window.getComputedStyle(layer))) found = true;
      });
    });
    return found;
  }

  function paintedBackgroundColor(element) {
    var rect = element.getBoundingClientRect();
    var node = element;
    while (node && node.nodeType === 1) {
      var style = window.getComputedStyle(node);
      if (style.backgroundImage && style.backgroundImage !== 'none') return null;
      if (node !== element && hasCoveringLayer(node, rect)) return null;
      if (relativeLuminance(style.backgroundColor) !== null) return style.backgroundColor;
      node = node.parentElement;
    }
    return 'rgb(255, 255, 255)';
  }


  function shiftedForeground(channels, background, target, darken) {
    for (var step = 1; step <= 20; step++) {
      var shifted = channels.map(function (value) {
        return darken
          ? Math.round(value * (1 - step * 0.05))
          : Math.round(value + (255 - value) * step * 0.05);
      });
      var candidate = 'rgb(' + shifted.join(', ') + ')';
      if (contrastRatio(candidate, background) >= target) return candidate;
    }
    return null;
  }

  function readableForeground(foreground, background, target) {
    var parts = String(foreground).match(/[\d.]+/g);
    if (!parts || parts.length < 3) return null;
    var channels = parts.slice(0, 3).map(Number);

    var darken = relativeLuminance(background) > relativeLuminance(foreground);
    return (
      shiftedForeground(channels, background, target, darken) ||
      shiftedForeground(channels, background, target, !darken)
    );
  }

  function applyTextContrast(elements) {
    each(elements, function (el) {
      if (!renderedText(el).trim()) return;

      var style = window.getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none') return;

      var background = paintedBackgroundColor(el);
      if (background === null) return;

      var foreground = style.color;
      if (relativeLuminance(foreground) === null) return;

      var size = parseFloat(style.fontSize) || 16;
      var weight = parseInt(style.fontWeight, 10) || 400;
      var large = size >= 24 || (size >= 18.66 && weight >= 700);
      var target = large ? 3 : 4.5;

      var ratio = contrastRatio(foreground, background);
      if (ratio === null || ratio >= target) return;

      var original = el.getAttribute('data-a11y-original-color') || foreground;
      var candidate = readableForeground(original, background, target);
      if (!candidate) return;

      if (!el.hasAttribute('data-a11y-original-color')) el.setAttribute('data-a11y-original-color', original);
      el.style.setProperty('color', candidate, 'important');
      el.setAttribute('data-a11y-text-contrast', 'adjusted');
    });
  }

  var EMBEDDED_BUTTONS = '.lp-button-react, .continue-button, .back-button, .tpb-form button';

  var BRAND_INK = 'rgb(13, 32, 46)';
  var BRAND_PAPER = 'rgb(255, 255, 255)';

  function brandForeground(background, target) {
    if (contrastRatio(BRAND_INK, background) >= target) return BRAND_INK;
    if (contrastRatio(BRAND_PAPER, background) >= target) return BRAND_PAPER;
    return null;
  }

  function blendOver(color, backdrop, alpha) {
    var front = String(color).match(/[\d.]+/g);
    var behind = String(backdrop).match(/[\d.]+/g);
    if (!front || front.length < 3 || !behind || behind.length < 3) return null;
    if (front.length > 3 && Number(front[3]) < 1) return null;

    return (
      'rgb(' +
      front
        .slice(0, 3)
        .map(function (value, index) {
          return Math.round(Number(value) * alpha + Number(behind[index]) * (1 - alpha));
        })
        .join(', ') +
      ')'
    );
  }

  function flattenDilutedBackground(button, style, background, target) {
    if (button.hasAttribute('data-a11y-flattened')) return null;

    var alpha = parseFloat(style.opacity);
    if (!(alpha > 0 && alpha < 1)) return null;

    var parent = button.parentElement;
    var behind = parent ? paintedBackgroundColor(parent) : null;
    if (behind === null) return null;

    var flatBackground = blendOver(background, behind, alpha);
    var flatForeground = blendOver(style.color, behind, alpha);
    if (!flatBackground || !flatForeground) return null;

    var ratio = contrastRatio(flatForeground, flatBackground);
    if (ratio === null || ratio >= target) return null;

    var transition = button.style.transition;
    button.style.setProperty('transition', 'none', 'important');
    button.style.setProperty('background-color', flatBackground, 'important');
    button.style.setProperty('opacity', '1', 'important');
    void button.offsetHeight;
    button.style.transition = transition;
    button.setAttribute('data-a11y-flattened', 'true');
    return flatBackground;
  }

  function enforceEmbeddedButtonContrast(root) {
    each(root.querySelectorAll(EMBEDDED_BUTTONS), function (button) {
      if (button.hasAttribute('data-a11y-text-contrast')) return;
      if (!renderedText(button).trim()) return;

      var style = window.getComputedStyle(button);
      if (style.visibility === 'hidden' || style.display === 'none') return;

      var background = paintedBackgroundColor(button);
      if (background === null) return;

      var foreground = style.color;
      if (relativeLuminance(foreground) === null) return;

      var size = parseFloat(style.fontSize) || 16;
      var weight = parseInt(style.fontWeight, 10) || 400;
      var target = size >= 24 || (size >= 18.66 && weight >= 700) ? 3 : 4.5;

      background = flattenDilutedBackground(button, style, background, target) || background;

      var ratio = contrastRatio(foreground, background);
      if (ratio === null || ratio >= target) return;

      var candidate =
        brandForeground(background, target) ||
        readableForeground(foreground, background, target);
      if (!candidate) return;

      var transition = button.style.transition;
      button.style.setProperty('transition', 'none', 'important');
      button.style.setProperty('color', candidate, 'important');
      void button.offsetHeight;
      button.style.transition = transition;
      button.setAttribute('data-a11y-text-contrast', 'adjusted');
    });
  }

  var errorMessageCount = 0;

  function unlabelErrorMessages(root) {
    each(root.querySelectorAll('label[for][class*="error"]'), function (label) {
      if (label.hasAttribute('data-a11y-error-message')) return;

      var field = document.getElementById(label.htmlFor);
      if (!field || !field.labels || field.labels.length < 2) return;

      if (!label.id) {
        errorMessageCount++;
        label.id = 'a11y-error-message-' + errorMessageCount;
      }

      var described = (field.getAttribute('aria-describedby') || '').split(/\s+/);
      var known = [];
      for (var i = 0; i < described.length; i++) {
        if (described[i]) known.push(described[i]);
      }
      if (known.indexOf(label.id) === -1) known.push(label.id);

      field.setAttribute('aria-describedby', known.join(' '));
      label.removeAttribute('for');
      label.setAttribute('data-a11y-error-message', 'described');
    });
  }

  function enforceContentTextContrast(root) {
    each(root.querySelectorAll(CONTENT_SCOPES), function (scope) {
      applyTextContrast(scope.querySelectorAll('[style*="color"]'));
    });

    each(root.querySelectorAll(LINK_CONTRAST_SCOPES), function (scope) {
      applyTextContrast(scope.querySelectorAll('a'));
    });
  }

  function distinguishInTextLinks(root) {
    each(root.querySelectorAll(CONTENT_SCOPES), function (scope) {
      each(scope.querySelectorAll('a[href]'), function (link) {
        if (link.hasAttribute('data-a11y-underlined')) return;
        if (!(link.textContent || '').trim()) return;

        var style = window.getComputedStyle(link);
        if (style.display !== 'inline' && style.display !== 'inline-block') return;
        if (style.textDecorationLine && style.textDecorationLine !== 'none') return;
        if (relativeLuminance(style.backgroundColor) !== null) return;
        if (parseFloat(style.borderBottomWidth) > 0) return;

        var decorated = false;
        each(link.querySelectorAll('*'), function (child) {
          if (decorated) return;
          var childStyle = window.getComputedStyle(child);
          if (childStyle.textDecorationLine.indexOf('underline') !== -1) decorated = true;
          if (parseFloat(childStyle.borderBottomWidth) > 0) decorated = true;
        });
        if (decorated) return;

        var context = link.parentElement;
        if (!context) return;

        var ratio = contrastRatio(style.color, window.getComputedStyle(context).color);
        if (ratio === null || ratio >= 3) return;

        link.setAttribute('data-a11y-underlined', 'true');
      });
    });
  }

  function labelIconButtons(root) {
    each(root.querySelectorAll('.flickity-button[aria-label] > svg:not([title])'), function (svg) {
      svg.setAttribute('title', svg.parentNode.getAttribute('aria-label'));
    });
  }

  function labelExtraMains(root) {
    each(root.querySelectorAll('main, [role="main"]'), function (main) {
      if (main.getAttribute('aria-labelledby')) return;
      var doc = main.ownerDocument;
      var span = doc.createElement('span');
      span.id = 'a11y-main-label-' + (++a11yIdCounter);
      span.hidden = true;
      span.textContent = (main.getAttribute('aria-label') || 'Main content') + ' ' + a11yIdCounter;
      main.insertBefore(span, main.firstChild);
      main.setAttribute('aria-labelledby', span.id);
    });

    each(root.querySelectorAll('iframe'), function (frame) {
      var frameDoc = null;
      try {
        frameDoc = frame.contentDocument;
      } catch (e) {
        frameDoc = null;
      }
      if (frameDoc && frameDoc.body) labelExtraMains(frameDoc);
    });
  }

  function demoteGalleryTabstops(root) {
    each(root.querySelectorAll('.product-gallery__main.flickity-enabled[tabindex="0"], .product-gallery__thumbnails.flickity-enabled[tabindex="0"], .products-slider.flickity-enabled[tabindex="0"]:not([role])'), function (gallery) {
      gallery.setAttribute('tabindex', '-1');
    });
  }

  function syncOverlayHeaderState() {
    var wrapper = document.querySelector('[data-enable_overlay="true"]');
    if (!wrapper) return;

    var container = document.querySelector('[data-check-for-order="true"]');
    if (!container) return;

    var first = container.querySelector('[id^="shopify-section"]');
    var underlayIsImage =
      !!first &&
      first.classList.contains('overlaid-header-option') &&
      first.innerHTML.trim().length > 0;

    if (!underlayIsImage) wrapper.setAttribute('data-enable_overlay', 'false');
  }

  function unlinkNoJsNavLabels(root) {
    each(root.querySelectorAll('label[for^="mega-"], label[for^="dropdown-"]'), function (label) {
      var target = document.getElementById(label.getAttribute('for'));
      if (target && !target.classList.contains('no-js-main-navigation')) return;
      label.removeAttribute('for');
    });
  }

  function fieldNameFromSelect(select) {
    if (!select) return '';
    if (select.labels && select.labels.length) {
      for (var i = 0; i < select.labels.length; i++) {
        var text = renderedText(select.labels[i]).trim();
        if (text) return text;
      }
    }
    var aria = (select.getAttribute('aria-label') || '').trim();
    if (aria) return aria;
    if (select.options && select.options.length) {
      return (select.options[0].text || '').trim();
    }
    return '';
  }

  function linkSelectLabels(root) {
    each(root.querySelectorAll('select:not([aria-labelledby]):not([aria-label])'), function (select) {
      if (!select.labels) return;
      for (var i = 0; i < select.labels.length; i++) {
        var label = select.labels[i];
        if (label.contains(select) || !label.getClientRects().length) continue;
        if (!renderedText(label).trim()) continue;
        if (!label.id) label.id = 'a11y-select-label-' + (++a11yIdCounter);
        select.setAttribute('aria-labelledby', label.id);
        return;
      }
    });
  }

  function nameAriaListboxes(root) {
    each(root.querySelectorAll('[role="listbox"], [role="combobox"]'), function (box) {
      if (box.getAttribute('aria-label') || box.getAttribute('aria-labelledby')) return;

      var widget = box.closest('.ss-main') || box;
      var container = widget.parentElement;
      if (!container) return;

      var name = '';
      var label = container.querySelector('label');
      if (label && !label.contains(box)) name = renderedText(label).trim();
      if (!name) name = fieldNameFromSelect(container.querySelector('select'));
      if (!isMeaningfulLabel(name)) return;

      box.setAttribute('aria-label', name.replace(/\s+/g, ' '));
    });
  }

  var TOGGLE_GROUPS = '.tpb-form-control, .gpo-group, .form-field, .field, .input-group';

  function labelledFieldName(field) {
    if (field.labels) {
      for (var i = 0; i < field.labels.length; i++) {
        if (renderedText(field.labels[i]).trim()) return true;
      }
    }
    if ((field.getAttribute('aria-label') || '').trim()) return true;
    if (field.getAttribute('aria-labelledby')) return true;
    if ((field.getAttribute('title') || '').trim()) return true;
    return false;
  }

  function nameWrappedToggles(root) {
    each(root.querySelectorAll('input[type="checkbox"], input[type="radio"]'), function (input) {
      if (labelledFieldName(input)) return;

      var name = '';
      var node = input.parentElement;
      for (var depth = 0; node && depth < 4 && !isMeaningfulLabel(name); depth++) {
        if (node.tagName === 'LABEL' || node.matches(TOGGLE_GROUPS)) {
          name = renderedText(node).trim();
        }
        node = node.parentElement;
      }
      if (!isMeaningfulLabel(name)) return;

      input.setAttribute('aria-label', name.replace(/\s+/g, ' '));
    });
  }

  var OPTION_GROUPS = '.gpo-form__group';
  var optionGroupUid = 0;

  function groupOptionChoices(root) {
    each(root.querySelectorAll(OPTION_GROUPS), function (group) {
      var label = null;
      each(group.children, function (child) {
        if (!label && child.tagName === 'LABEL') label = child;
      });
      if (!label) return;
      if (label.getAttribute('for') === '') label.removeAttribute('for');

      var choices = group.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      if (choices.length < 2 || !renderedText(label).trim()) return;

      var last = choices[choices.length - 1];
      var container = choices[0].parentElement;
      while (container && container !== group && !container.contains(last)) {
        container = container.parentElement;
      }
      if (!container || container.closest('fieldset, [role="group"], [role="radiogroup"]')) return;

      if (!label.id) label.id = 'a11y-option-group-' + ++optionGroupUid;
      container.setAttribute('role', 'group');
      container.setAttribute('aria-labelledby', label.id);
    });
  }

  function authoredHeadingLevel(heading) {
    if (/^H[1-6]$/.test(heading.tagName)) {
      if (heading.hasAttribute('data-a11y-heading-level')) return Number(heading.tagName.charAt(1));
      var declared = parseInt(heading.getAttribute('aria-level'), 10);
      if (declared >= 1 && declared <= 6) return declared;
      return Number(heading.tagName.charAt(1));
    }
    var level = parseInt(heading.getAttribute('aria-level'), 10);
    return level >= 1 && level <= 6 ? level : 2;
  }

  function resequenceHeadingLevels(root) {
    var previous = 0;

    each(root.querySelectorAll('h1, h2, h3, h4, h5, h6, [role="heading"]'), function (heading) {
      if (!heading.getClientRects().length) return;
      if (!renderedText(heading).trim()) return;

      var authored = authoredHeadingLevel(heading);
      var level = previous && authored > previous + 1 ? previous + 1 : authored;
      previous = level;

      if (level === authored && !heading.hasAttribute('data-a11y-heading-level')) return;

      if (level === authored && /^H[1-6]$/.test(heading.tagName)) {
        heading.removeAttribute('aria-level');
        heading.removeAttribute('data-a11y-heading-level');
        return;
      }

      heading.setAttribute('aria-level', String(level));
      heading.setAttribute('data-a11y-heading-level', String(level));
    });
  }

  var LATE_FOCUS_STYLE_ID = 'a11y-late-focus-styles';
  var LATE_FOCUS_CSS =
    '.gpo-container select:focus,' +
    '.gpo-container input:focus,' +
    '.gpo-container textarea:focus,' +
    '.yotpo-widget-clear button:focus,' +
    '.yotpo-widget-clear a:focus,' +
    '.yotpo-widget-clear [tabindex]:focus' +
    '{outline-style:solid;outline-color:transparent;}';
  var lateFocusMoves = 0;

  function keepLateFocusStylesLast() {
    if (!document.body || lateFocusMoves > 20) return;
    var style = document.getElementById(LATE_FOCUS_STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = LATE_FOCUS_STYLE_ID;
      style.textContent = LATE_FOCUS_CSS;
    }
    var sheets = document.styleSheets;
    var last = sheets.length ? sheets[sheets.length - 1].ownerNode : null;
    if (last === style && style.parentNode === document.body) return;
    lateFocusMoves++;
    document.body.appendChild(style);
  }

  function nameStore() {
    var store = document.getElementById('a11y-name-store');
    if (!store) {
      store = document.createElement('div');
      store.id = 'a11y-name-store';
      store.hidden = true;
      document.body.appendChild(store);
    }
    return store;
  }

  function normalizedWords(text) {
    return (text || '').replace(/[^a-zA-Z0-9]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  var nameRefUid = 0;

  function alignWidgetButtonNames(root) {
    each(root.querySelectorAll('.yotpo-widget-clear button[aria-label]'), function (button) {
      var label = (button.getAttribute('aria-label') || '').trim();
      if (!label) return;

      var refId = button.getAttribute('data-a11y-name-ref');
      var ref = refId ? document.getElementById(refId) : null;
      if (!ref) {
        var visible = normalizedWords(button.innerText);
        if (visible.length < 2 || normalizedWords(label).indexOf(visible) !== -1) return;
        ref = document.createElement('span');
        ref.id = 'a11y-name-ref-' + ++nameRefUid;
        nameStore().appendChild(ref);
        button.setAttribute('data-a11y-name-ref', ref.id);
      }
      if (ref.textContent !== label) ref.textContent = label;
      if (button.getAttribute('aria-labelledby') !== ref.id) button.setAttribute('aria-labelledby', ref.id);
    });
  }

  function labelFrameLanguages(root) {
    var lang = document.documentElement.getAttribute('lang') || 'en';
    each(root.querySelectorAll('iframe'), function (frame) {
      var frameDoc = null;
      try {
        frameDoc = frame.contentDocument;
      } catch (e) {
        frameDoc = null;
      }
      if (!frameDoc || !frameDoc.documentElement) return;
      if (!frameDoc.documentElement.getAttribute('lang')) frameDoc.documentElement.setAttribute('lang', lang);
    });
  }

  /* ---------------------------------------------------------------------------
   * Runner
   * ------------------------------------------------------------------------ */
  function sweep() {
    var root = document;
    try {
      syncAriaHiddenFocusability(root);
      markDecorativeVideos(root);
      nameEmptyControls(root);
      nameAriaListboxes(root);
      linkSelectLabels(root);
      nameWrappedToggles(root);
      groupOptionChoices(root);
      fixImagesAndFrames(root);
      repairNestedLists(root);
      syncPlyrToggleNames(root);
      describeVagueLinks(root);
      warnExternalTargets(root);
      dedupeTopLevelHeadings(root);
      resequenceHeadingLevels(root);
      enforceAppButtonContrast(root);
      enforceEmbeddedButtonContrast(root);
      enforceContentTextContrast(root);
      unlabelErrorMessages(root);
      distinguishInTextLinks(root);
      markEmptyHeadings(root);
      syncOverlayHeaderState();
      unlinkNoJsNavLabels(root);
      exposeNavTriggers(root);
      syncNavTriggerState();
      labelIconButtons(root);
      labelExtraMains(root);
      demoteGalleryTabstops(root);
      alignWidgetButtonNames(root);
      labelFrameLanguages(root);
      keepLateFocusStylesLast();
    } catch (e) {
      if (window.console && console.warn) console.warn('[a11y] sweep failed:', e);
    }
  }

  var pending = null;
  function scheduleSweep() {
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      sweep();
    }, THROTTLE_MS);
  }

  function init() {
    bindThumbnailKeys();
    bindNavExpandedState();
    bindNavTriggerKeys();
    sweep();

    if (!('MutationObserver' in window)) return;

    // Flickity adds aria-hidden/tabindex/class long after DOMContentLoaded, and
    // the storefront apps mount whenever their scripts finish - so watch both
    // structure and the specific attributes that drive the rules above.
    //
    // `class` has to be watched because Flickity only adds its cell classes once
    // it initialises, but plyr also rewrites class names continuously while a
    // video plays. Filtering here keeps that noise from scheduling a sweep every
    // 200ms for the whole length of a video.
    var CLASS_WATCH = '.flickity-enabled, .flickity-viewport, .gallery-cell, .product-gallery__thumbnail, .product-gallery__image, .mega-menu__section';

    function isRelevant(mutation) {
      if (mutation.type === 'childList') return mutation.addedNodes.length > 0;
      if (mutation.attributeName !== 'class') return true;
      var target = mutation.target;
      if (!target || typeof target.matches !== 'function') return false;
      return target.matches(CLASS_WATCH) || !!target.closest(CLASS_WATCH);
    }

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (isRelevant(mutations[i])) {
          try {
            syncAriaHiddenFocusability(document);
            enforceAppButtonContrast(document);
            fixImagesAndFrames(document);
          } catch (e) {
            if (window.console && console.warn) console.warn('[a11y] immediate pass failed:', e);
          }
          scheduleSweep();
          return;
        }
      }
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'tabindex', 'class', 'src', 'title', 'href', 'aria-label', 'role', 'for'],
    });

    window.addEventListener('load', scheduleSweep);

    var frameTicks = 0;
    var frameTimer = window.setInterval(function () {
      try {
        labelExtraMains(document);
        labelFrameLanguages(document);
        enforceContentTextContrast(document);
      } catch (e) {
        if (window.console && console.warn) console.warn('[a11y] frame sweep failed:', e);
      }
      if (++frameTicks >= 30) window.clearInterval(frameTimer);
    }, 2000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
